const CropItem = ({ name, value, imageUrl, barColor, totalCrops }) => {
  const maxValue = totalCrops[0]?.value || 1;
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;

  return (
    <div className="d-flex align-items-center mb-3">
      <div className="me-3" style={{ flexShrink: 0 }}>
        <div
          style={{
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
          }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={name}
              style={{
                width: '24px',
                height: '24px',
                objectFit: 'contain',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `
                  <div style="
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background-color: ${barColor};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: bold;
                    font-size: 10px;
                  ">
                    ${name.charAt(0)}
                  </div>
                `;
              }}
            />
          ) : (
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: barColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '10px',
              }}
            >
              {name.charAt(0)}
            </div>
          )}
        </div>
      </div>

      <div className="flex-grow-1 min-w-0">
        <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
          <span
            className="fw-medium text-truncate"
            style={{ fontSize: '14px' }}
          >
            {name}
          </span>
          <span
            className="fw-bold flex-shrink-0"
            style={{
              color: barColor,
              fontSize: '14px',
              minWidth: '40px',
              textAlign: 'right',
            }}
          >
            {value}
          </span>
        </div>

        <div className="progress" style={{ height: '6px', backgroundColor: '#f0f0f0' }}>
          <div
            className="progress-bar"
            style={{
              width: `${percentage}%`,
              backgroundColor: barColor,
              borderRadius: '3px',
            }}
          />
        </div>
      </div>
    </div>
  );
};

const TopCropsRanking = ({ topCrops = [], topVariants = [] }) => {
  const cropColors = {
    Corn: '#FF9800',
    Rice: '#4CAF50',
    Wheat: '#FFC107',
    Potato: '#795548',
    Tomato: '#E91E63',
  };

  const fallbackImages = {
    Apple: 'https://cdn-icons-png.flaticon.com/512/415/415682.png',
  };

  const getImageUrl = (crop) => {
    if (crop.Image) {
      if (crop.Image.startsWith('http')) {
        return crop.Image;
      }
      return `https://api.soksan-dss.com/uploads/crop/${crop.Image}`;
    }

    const cropName = crop.CropName || crop.VariantName || '';
    const normalizedName = cropName.toLowerCase();

    for (const [key, url] of Object.entries(fallbackImages)) {
      if (normalizedName.includes(key.toLowerCase())) {
        return url;
      }
    }

    return 'https://cdn-icons-png.flaticon.com/512/415/415682.png';
  };

  const getColorForCrop = (cropName) => {
    const name = cropName || '';

    if (cropColors[name]) {
      return cropColors[name];
    }

    const lowerName = name.toLowerCase();
    for (const [key, color] of Object.entries(cropColors)) {
      if (lowerName.includes(key.toLowerCase())) {
        return color;
      }
    }

    const colors = [
      '#4CAF50',
      '#2196F3',
      '#FF9800',
      '#F44336',
      '#9C27B0',
      '#00BCD4',
      '#FFC107',
      '#795548',
      '#607D8B',
      '#E91E63',
      '#8d4798ff',
      '#009688',
      '#8BC34A',
      '#CDDC39',
      '#FF5722',
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  const allCrops = [
    ...topCrops.map((crop) => ({
      id: crop.CropID,
      name: crop.CropName,
      value: crop.Count || 0,
      imageUrl: getImageUrl(crop),
      barColor: getColorForCrop(crop.CropName),
      type: 'crop',
    })),
    ...topVariants.map((variant) => ({
      id: variant.VariantID,
      name: variant.VariantName,
      value: variant.Count || 0,
      imageUrl: getImageUrl(variant),
      barColor: getColorForCrop(variant.CropName),
      type: 'variant',
    })),
  ]
    .filter((crop) => crop.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  if (allCrops.length === 0) {
    return (
      <div className="col-12 col-xl-6">
        <div className="card h-100">
          <div className="card-body">
            <h6 className="text-lg mb-4" style={{ color: '#1B5E20', fontWeight: 'bold' }}>
              Top Crops
            </h6>
            <div className="text-center py-4">
              <div className="mb-3">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/1046/1046769.png"
                  alt="No crops"
                  style={{ width: '60px', opacity: 0.5 }}
                />
              </div>
              <p className="text-muted mb-0">No crop data available</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12 col-xl-6">
      <div className="card h-100">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h6 className="text-lg mb-0" style={{ color: '#1B5E20', fontWeight: 'bold' }}>
              Top Crops
            </h6>
          </div>

          <div className="d-flex flex-column">
            {allCrops.slice(0, 5).map((crop, index) => (
              <CropItem
                key={`${crop.id}-${index}`}
                name={crop.name}
                value={crop.value}
                imageUrl={crop.imageUrl}
                barColor={crop.barColor}
                totalCrops={allCrops}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopCropsRanking;
